/**
 * Two deliberate departures from other bridges in this repo:
 *
 *   1. Command names are bare (no `plugin:...|` prefix) because the
 *      engine plugin is Tauri-agnostic; the `#[tauri::command]` wrappers
 *      live in the main app at `src-tauri/src/rpa_recording/commands.rs`.
 *   2. Methods return `Result`-shaped types instead of throwing — recorder
 *      failures (permissions denied, already recording) are expected
 *      flow, not exceptional, so the consumer must always handle them.
 */

import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';

import { isTauri } from '../utils';

import {
  RPA_RECORDER_PROTOCOL,
  type AgentPermissionAckRequest,
  type AgentPermissionRequestedPayload,
  type ArmStateChangedPayload,
  type ArmStatePayload,
  type EngineCommandErrorPayload,
  type EngineSessionStatus,
  type FrameCaptureMissedPayload,
  type NarrationAudioLevelsPayload,
  type NarrationLevelBands,
  type NarrationMicStateChangedPayload,
  type PlatformHealthChangedPayload,
  type SessionStateChangedPayload,
  type ToolbarHoverChangedPayload,
} from './protocol';
import {
  normalizeRecordingDescription,
  RpaRecorderError,
  validateRecordingName,
  workflowFromRecord,
  type AgentModeMetadata,
  type AgentPermissionAckResult,
  type ArmResult,
  type DiscardResult,
  type RecordingEntrySurface,
  type RecordingRecord,
  type RecordingStartOptions,
  type RecordingTrayLabels,
  type RpaRecorderErrorCode,
  type RpaRecorderStatus,
  type RpaRecorderStatusListener,
  type SessionId,
  type SessionSummary,
  type StartResult,
  type StopResult,
  type SubmitRecordingNameResult,
  type Unsubscribe,
  type Workflow,
} from './types';

export { RPA_RECORDER_PROTOCOL } from './protocol';
export type {
  AgentPermissionAckRequest,
  AgentPermissionRequestedPayload,
  NarrationLevelBands,
} from './protocol';
export {
  DEFAULT_RECORDING_TRAY_LABELS,
  MAX_RECORDING_NAME_LEN,
  normalizeRecordingDescription,
  RPA_NARRATION_STATUS_OFF,
  RpaRecorderError,
  validateRecordingName,
  workflowFromRecord,
} from './types';
export type {
  AgentModeMetadata,
  AgentPermissionAckResult,
  ArmResult,
  Capability,
  DiscardResult,
  RecordingEntrySurface,
  RecordingRecord,
  RecordingStartOptions,
  RecordingTrayLabels,
  RpaRecorderErrorCode,
  RpaRecorderStatus,
  RpaRecorderStatusListener,
  SessionId,
  SessionSummary,
  StartResult,
  StopResult,
  SubmitRecordingNameResult,
  Unsubscribe,
  Workflow,
} from './types';

const RECORDING_START = RPA_RECORDER_PROTOCOL.commands.start;
const RECORDING_RESTART = RPA_RECORDER_PROTOCOL.commands.restart;
const RECORDING_STOP = RPA_RECORDER_PROTOCOL.commands.stop;
const RECORDING_CANCEL = RPA_RECORDER_PROTOCOL.commands.cancel;
const RECORDING_ARM = RPA_RECORDER_PROTOCOL.commands.arm;
const RECORDING_DISARM = RPA_RECORDER_PROTOCOL.commands.disarm;
const RECORDING_TOOLBAR_SET_MODE = RPA_RECORDER_PROTOCOL.commands.toolbarSetMode;
const RECORDING_ARM_STATE = RPA_RECORDER_PROTOCOL.commands.armState;
const RECORDING_STATUS = RPA_RECORDER_PROTOCOL.commands.status;
const RECORDING_LIST = RPA_RECORDER_PROTOCOL.commands.list;
const RECORDING_DISCARD = RPA_RECORDER_PROTOCOL.commands.discard;
const RECORDING_NAME_SUBMIT = RPA_RECORDER_PROTOCOL.commands.nameSubmit;
const RECORDING_AGENT_PERMISSION_ACK = RPA_RECORDER_PROTOCOL.commands.agentPermissionAck;

export const SESSION_STATE_CHANGED_EVENT =
  RPA_RECORDER_PROTOCOL.events.sessionStateChanged;

export const ARM_STATE_CHANGED_EVENT = RPA_RECORDER_PROTOCOL.events.armStateChanged;

export const PLATFORM_HEALTH_CHANGED_EVENT =
  RPA_RECORDER_PROTOCOL.events.platformHealthChanged;

export const FRAME_CAPTURE_MISSED_EVENT = RPA_RECORDER_PROTOCOL.events.frameCaptureMissed;

export const TOOLBAR_HOVER_CHANGED_EVENT =
  RPA_RECORDER_PROTOCOL.events.toolbarHoverChanged;

export const NATIVE_STOP_REQUESTED_EVENT =
  RPA_RECORDER_PROTOCOL.events.nativeStopRequested;

export const NARRATION_MIC_STATE_CHANGED_EVENT =
  RPA_RECORDER_PROTOCOL.events.narrationMicStateChanged;

export const NARRATION_AUDIO_LEVELS_EVENT =
  RPA_RECORDER_PROTOCOL.events.narrationAudioLevels;

export const AGENT_PERMISSION_REQUESTED_EVENT =
  RPA_RECORDER_PROTOCOL.events.agentPermissionRequested;

const ENGINE_ERROR_CODE_MAP: Record<string, RpaRecorderErrorCode> = {
  [RPA_RECORDER_PROTOCOL.engineErrors.permissionDenied]: 'PERMISSION_DENIED',
  [RPA_RECORDER_PROTOCOL.engineErrors.alreadyActive]: 'ALREADY_ACTIVE',
  [RPA_RECORDER_PROTOCOL.engineErrors.notActive]: 'NOT_ACTIVE',
  [RPA_RECORDER_PROTOCOL.engineErrors.sessionInFlight]: 'SESSION_IN_FLIGHT',
  [RPA_RECORDER_PROTOCOL.engineErrors.failedSessionPendingDiscard]:
    'FAILED_SESSION_PENDING_DISCARD',
  [RPA_RECORDER_PROTOCOL.engineErrors.stopTimedOut]: 'STOP_TIMED_OUT',
  [RPA_RECORDER_PROTOCOL.engineErrors.platformTapDisabled]: 'PLATFORM_TAP_DISABLED',
  [RPA_RECORDER_PROTOCOL.engineErrors.nameInvalid]: 'NAME_INVALID',
  [RPA_RECORDER_PROTOCOL.engineErrors.nameTooLong]: 'NAME_TOO_LONG',
  [RPA_RECORDER_PROTOCOL.engineErrors.notFound]: 'NOT_FOUND',
  [RPA_RECORDER_PROTOCOL.engineErrors.agentPermissionUnknownRequest]:
    'AGENT_PERMISSION_UNKNOWN_REQUEST',
  [RPA_RECORDER_PROTOCOL.engineErrors.agentPermissionMismatch]:
    'AGENT_PERMISSION_MISMATCH',
  [RPA_RECORDER_PROTOCOL.engineErrors.agentPermissionWrongWebview]:
    'AGENT_PERMISSION_WRONG_WEBVIEW',
  [RPA_RECORDER_PROTOCOL.engineErrors.windowFailed]: 'WINDOW_FAILED',
};

const STOP_RECOVERY_TIMEOUT_MS = 10_000;

function isEngineCommandError(value: unknown): value is EngineCommandErrorPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { code?: unknown }).code === 'string'
  );
}

/** Fallback copy when the engine IPC payload omits `message` (logs/dev only). */
function engineErrorMessage(code: RpaRecorderErrorCode): string {
  switch (code) {
    case 'PERMISSION_DENIED':
      return 'Recording permissions are missing.';
    case 'ALREADY_ACTIVE':
      return 'A recording session is already active.';
    case 'NOT_ACTIVE':
      return 'No recording session is active.';
    case 'SESSION_IN_FLIGHT':
      return 'The recording session is still active.';
    case 'FAILED_SESSION_PENDING_DISCARD':
      return 'The previous recording failed and must be discarded before starting a new one.';
    case 'STOP_TIMED_OUT':
      return 'Recording stop timed out.';
    case 'PLATFORM_TAP_DISABLED':
      return 'Recording input capture was disabled by the platform.';
    case 'NAME_INVALID':
      return 'Recording name cannot be empty.';
    case 'NAME_TOO_LONG':
      return 'Recording name is too long.';
    case 'NOT_FOUND':
      return 'Recording session was not found.';
    case 'WINDOW_FAILED':
      return 'Recording window operation failed.';
    case 'AGENT_PERMISSION_UNKNOWN_REQUEST':
      return 'The agent permission request is no longer pending.';
    case 'AGENT_PERMISSION_MISMATCH':
      return 'The agent permission acknowledgement does not match the pending request.';
    case 'AGENT_PERMISSION_WRONG_WEBVIEW':
      return 'Agent permission acknowledgements only come from the main window.';
    case 'BRIDGE_UNAVAILABLE':
      return 'RpaRecorder is only available inside the Tauri runtime';
    case 'UNKNOWN':
      return 'Recording operation failed.';
  }
}

function recordingErrorFromUnknown(raw: unknown): RpaRecorderError {
  if (raw instanceof RpaRecorderError) return raw;
  if (isEngineCommandError(raw)) {
    const code = ENGINE_ERROR_CODE_MAP[raw.code] ?? 'UNKNOWN';
    const message =
      typeof raw.message === 'string' && raw.message.length > 0
        ? raw.message
        : engineErrorMessage(code);
    return new RpaRecorderError(code, message, {
      missing: raw.missing,
      cause: raw,
    });
  }
  if (raw instanceof Error) {
    return new RpaRecorderError('UNKNOWN', raw.message, { cause: raw });
  }
  return new RpaRecorderError('UNKNOWN', String(raw), { cause: raw });
}

function armedStatus(agent: AgentModeMetadata | null | undefined): RpaRecorderStatus {
  return { kind: 'armed', agent: agent ?? null };
}

export type RecordingToolbarMode =
  | 'controlBar'
  | 'controlBarHover'
  | 'terminal'
  | 'confirmCancel';

export interface RpaRecorderBridge {
  isAvailable(): boolean;
  getStatus(): RpaRecorderStatus;
  /** Pair with `getStatus()` for a `useSyncExternalStore`-compatible store. */
  subscribe(listener: RpaRecorderStatusListener): Unsubscribe;
  /** Idempotent. Call once at app bootstrap before any consumer reads status. */
  init(): Promise<void>;
  cleanup(): Promise<void>;
  /**
   * Shows the recording toolbar window (hiding the overlay) without starting
   * the capture engine, moving the FSM to `armed`. The user then begins
   * recording from the toolbar's record button. Idempotent when already armed.
   * `entrySurface` is latched so stop/disarm restore the window the user came from.
   */
  arm(entrySurface: RecordingEntrySurface): Promise<ArmResult>;
  /**
   * Reverses `arm()` — restores the entry surface the user came from and
   * returns the FSM to `idle`. Idempotent when already idle; rejected while a
   * session is active.
   */
  disarm(): Promise<ArmResult>;
  /**
   * Resolves only after the FSM has applied the matching `Started` event,
   * so `getStatus()` after a successful `await` is guaranteed `recording`.
   * Callable from `idle` (direct start) or `armed` (toolbar record button).
   * When starting directly from idle, pass `options.entrySurface` so stop/disarm
   * can return to the correct window; omit it when the surface was set by `arm()`.
   */
  start(
    name: string,
    trayLabels: RecordingTrayLabels,
    options?: RecordingStartOptions
  ): Promise<StartResult>;
  /**
   * Restarts the active recording in place: the engine discards the current
   * take and begins a fresh session under a new id, emitting a single
   * `Started` (no `Stopped`, so the toolbar stays put and no upload is queued
   * for the discarded take). Resolves only after the FSM has applied that
   * `Started`, so `getStatus()` after a successful `await` is `recording` with
   * the new session id and a reset `startedAt`. Only callable while `recording`.
   */
  restart(
    name: string,
    trayLabels: RecordingTrayLabels,
    options?: RecordingStartOptions
  ): Promise<StartResult>;
  stop(): Promise<StopResult>;
  /**
   * Discards the current active take and returns the recorder to the armed
   * toolbar state. This is distinct from `disarm()`, which leaves recorder mode
   * and restores the overlay.
   */
  cancelActiveRecording(): Promise<ArmResult>;
  listRecordings(): Promise<readonly Workflow[]>;
  discardRecording(sessionId: SessionId): Promise<DiscardResult>;
  discardRecordingWhenReady(sessionId: SessionId): Promise<DiscardResult>;
  submitRecordingName(
    sessionId: SessionId,
    name: string,
    description?: string | null
  ): Promise<SubmitRecordingNameResult>;
  /**
   * Discards the pending failed session reported by the engine and starts a
   * fresh recording. Used by the failed-session recovery sheet — the engine
   * omits the failed session id from start-error payloads, so this method
   * fetches it via `recording_status` before discarding.
   */
  recoverFailedSessionAndStart(
    name: string,
    trayLabels: RecordingTrayLabels,
    options?: RecordingStartOptions
  ): Promise<StartResult>;
  /**
   * Applies a semantic toolbar panel mode. Rust owns native NSPanel dimensions
   * for each mode; frontend code only reports which UI surface it is showing.
   */
  setToolbarMode(mode: RecordingToolbarMode): Promise<void>;
  /**
   * Subscribes to the toolbar panel's native hover signal. The NSPanel's
   * `NSTrackingArea` is the authoritative pointer-truth source — it fires across
   * programmatic panel resizes, unlike DOM `mouseenter`/`mouseleave`, which stick
   * once the panel is resized out from under the cursor. Resolves to a no-op
   * unsubscribe outside the Tauri runtime.
   */
  subscribeToolbarHover(listener: (hovering: boolean) => void): Promise<Unsubscribe>;
  /**
   * Subscribes to native-triggered stop requests: the tray "Stop Recording"
   * item and the global stop shortcut. Native code does not call the engine's
   * stop directly for these — that would bypass the short-recording discard
   * policy and naming prompt in `stopRecordingForSave` — so it asks whichever
   * webview owns the active recording UI to run its own `stop()` instead.
   * Resolves to a no-op unsubscribe outside the Tauri runtime.
   */
  subscribeNativeStopRequested(listener: () => void): Promise<Unsubscribe>;
  /**
   * Subscribes to the narration mic's five-band level meter (~30 fps, each band
   * 0–50). Deliberately bypasses `RpaRecorderStatus`: at this cadence a status
   * transition per frame would re-render every toolbar consumer, so subscribers
   * are expected to write straight to the DOM. Resolves to a no-op unsubscribe
   * outside the Tauri runtime.
   */
  subscribeNarrationLevels(
    listener: (levels: NarrationLevelBands) => void
  ): Promise<Unsubscribe>;
  /**
   * Subscribes to correlated agent permission requests. Only the main window
   * mounts the permission host.
   */
  subscribeAgentPermissionRequested(
    listener: (request: AgentPermissionRequestedPayload) => void
  ): Promise<Unsubscribe>;
  /**
   * Answers a correlated agent permission request. Rust rejects stale,
   * mismatched, or wrong-webview acks.
   */
  acknowledgeAgentPermission(
    ack: AgentPermissionAckRequest
  ): Promise<AgentPermissionAckResult>;
}

interface InvokeApi {
  invoke<T>(command: string, payload?: Record<string, unknown>): Promise<T>;
}
interface EventApi {
  listen<T>(
    eventName: string,
    handler: (event: { payload: T }) => void
  ): Promise<() => void>;
}

interface BridgeDeps {
  isTauri: () => boolean;
  loadInvoke: () => Promise<InvokeApi>;
  loadEvent: () => Promise<EventApi>;
  now: () => number;
}

const defaultDeps: BridgeDeps = {
  isTauri,
  loadInvoke: () => import('@tauri-apps/api/core'),
  loadEvent: () => import('@tauri-apps/api/event'),
  now: () => Date.now(),
};

interface PendingTransition {
  readonly sessionId: SessionId;
  readonly resolve: () => void;
  readonly reject: (error: RpaRecorderError) => void;
}

class RpaRecorderBridgeImpl implements RpaRecorderBridge {
  private status: RpaRecorderStatus = { kind: 'idle' };
  private readonly listeners = new Set<RpaRecorderStatusListener>();
  private unlistenSessionState: (() => void) | null = null;
  private unlistenArmState: (() => void) | null = null;
  private unlistenPlatformHealth: (() => void) | null = null;
  private unlistenFrameCaptureMissed: (() => void) | null = null;
  private initPromise: Promise<void> | null = null;

  private pendingStart: PendingTransition | null = null;
  private pendingStop: PendingTransition | null = null;
  private stopRecoveryTimeout: ReturnType<typeof setTimeout> | null = null;

  // Seeds for Started events that race ahead of start/restart invoke responses.
  private startedSessionSeed: {
    name: string;
    captureNarration: boolean;
    agent?: AgentModeMetadata | null;
  } | null = null;

  private pendingNarrationMicLive = new Map<SessionId, boolean>();

  private unlistenNarrationMic: (() => void) | null = null;

  private invokeApiCache: Promise<InvokeApi> | null = null;
  private eventApiCache: Promise<EventApi> | null = null;

  constructor(private readonly deps: BridgeDeps = defaultDeps) {}

  private getInvokeApi(): Promise<InvokeApi> {
    return (this.invokeApiCache ??= this.deps.loadInvoke());
  }
  private async getEventApi(): Promise<EventApi> {
    const pending = (this.eventApiCache ??= this.deps.loadEvent());
    try {
      return await pending;
    } catch (error) {
      if (this.eventApiCache === pending) this.eventApiCache = null;
      throw error;
    }
  }

  isAvailable(): boolean {
    return this.deps.isTauri();
  }

  getStatus(): RpaRecorderStatus {
    return this.status;
  }

  subscribe(listener: RpaRecorderStatusListener): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private clearStopRecoveryTimeout(): void {
    if (this.stopRecoveryTimeout === null) return;
    clearTimeout(this.stopRecoveryTimeout);
    this.stopRecoveryTimeout = null;
  }

  private scheduleStopRecovery(sessionId: SessionId): void {
    this.clearStopRecoveryTimeout();
    this.stopRecoveryTimeout = setTimeout(() => {
      this.stopRecoveryTimeout = null;
      if (this.status.kind !== 'stopping' || this.status.sessionId !== sessionId) {
        return;
      }
      const error = new RpaRecorderError(
        'STOP_TIMED_OUT',
        'Timed out waiting for the recorder to confirm stop'
      );
      this.transition({ kind: 'failed', sessionId, error });
      if (this.pendingStop && this.pendingStop.sessionId === sessionId) {
        this.pendingStop.reject(error);
        this.pendingStop = null;
      }
    }, STOP_RECOVERY_TIMEOUT_MS);
  }

  private scheduleStopEventFallback(sessionId: SessionId): void {
    this.clearStopRecoveryTimeout();
    this.stopRecoveryTimeout = setTimeout(() => {
      this.stopRecoveryTimeout = null;
      if (this.status.kind !== 'stopping' || this.status.sessionId !== sessionId) {
        return;
      }
      this.transition({ kind: 'idle' });
      if (this.pendingStop && this.pendingStop.sessionId === sessionId) {
        this.pendingStop.resolve();
        this.pendingStop = null;
      }
    }, STOP_RECOVERY_TIMEOUT_MS);
  }

  init(): Promise<void> {
    if (!this.deps.isTauri()) return Promise.resolve();
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.runInit();
    return this.initPromise;
  }

  private async runInit(): Promise<void> {
    const [eventApi, invokeApi] = await Promise.all([
      this.getEventApi(),
      this.getInvokeApi(),
    ]);
    // Listener must be live before the status fetch so events firing
    // mid-init aren't lost.
    this.unlistenSessionState = await eventApi.listen<SessionStateChangedPayload>(
      SESSION_STATE_CHANGED_EVENT,
      (event) => {
        this.handleSessionStateEvent(event.payload);
      }
    );
    this.unlistenArmState = await eventApi.listen<ArmStateChangedPayload>(
      ARM_STATE_CHANGED_EVENT,
      (event) => {
        this.handleArmStateEvent(event.payload);
      }
    );
    this.unlistenPlatformHealth = await eventApi.listen<PlatformHealthChangedPayload>(
      PLATFORM_HEALTH_CHANGED_EVENT,
      (event) => {
        this.handlePlatformHealthEvent(event.payload);
      }
    );
    this.unlistenFrameCaptureMissed = await eventApi.listen<FrameCaptureMissedPayload>(
      FRAME_CAPTURE_MISSED_EVENT,
      (event) => {
        this.handleFrameCaptureMissedEvent(event.payload);
      }
    );
    this.unlistenNarrationMic = await eventApi.listen<NarrationMicStateChangedPayload>(
      NARRATION_MIC_STATE_CHANGED_EVENT,
      (event) => {
        this.handleNarrationMicStateEvent(event.payload);
      }
    );

    try {
      const engineStatus = await invokeApi.invoke<EngineSessionStatus>(RECORDING_STATUS);
      this.applyEngineStatusSeed(engineStatus);
    } catch (raw) {
      logger.warn('[rpa-recorder] init: status seed failed', {
        code: recordingErrorFromUnknown(raw).code,
      });
    }

    // `armed` is a UI-only latch the engine status doesn't carry (it reports
    // `idle` while armed). The toolbar webview is created hidden and its JS may
    // not run until the panel is first shown — the same instant `recording_arm`
    // emits `arm_state_changed` — so it can miss that event. Seeding from the
    // backend latch makes `armed` recoverable on init, exactly like `recording`.
    try {
      const armState = await invokeApi.invoke<ArmStatePayload>(RECORDING_ARM_STATE);
      this.applyArmStateSeed(armState);
    } catch (raw) {
      logger.warn('[rpa-recorder] init: arm-state seed failed', {
        code: recordingErrorFromUnknown(raw).code,
      });
    }
  }

  async cleanup(): Promise<void> {
    // Wait for any in-flight init so we can unsubscribe the listener it
    // attached. Without this, a fast remount (Strict Mode) leaks the
    // listener and the next init() doubles up.
    const inflightInit = this.initPromise;
    this.initPromise = null;
    if (inflightInit) {
      await inflightInit.catch(() => {});
    }
    if (this.unlistenSessionState) {
      this.unlistenSessionState();
      this.unlistenSessionState = null;
    }
    if (this.unlistenArmState) {
      this.unlistenArmState();
      this.unlistenArmState = null;
    }
    if (this.unlistenPlatformHealth) {
      this.unlistenPlatformHealth();
      this.unlistenPlatformHealth = null;
    }
    if (this.unlistenFrameCaptureMissed) {
      this.unlistenFrameCaptureMissed();
      this.unlistenFrameCaptureMissed = null;
    }
    if (this.unlistenNarrationMic) {
      this.unlistenNarrationMic();
      this.unlistenNarrationMic = null;
    }
    if (this.pendingStart) {
      this.pendingStart.reject(
        new RpaRecorderError('UNKNOWN', 'Bridge cleaned up while starting')
      );
      this.pendingStart = null;
    }
    if (this.pendingStop) {
      this.pendingStop.reject(
        new RpaRecorderError('UNKNOWN', 'Bridge cleaned up while stopping')
      );
      this.pendingStop = null;
    }
    this.pendingNarrationMicLive.clear();
    if (this.status.kind !== 'idle') {
      this.transition({ kind: 'idle' });
    }
  }

  async arm(entrySurface: RecordingEntrySurface): Promise<ArmResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    if (this.status.kind === 'armed') return { ok: true };
    if (this.status.kind !== 'idle') {
      return {
        ok: false,
        error: new RpaRecorderError(
          'ALREADY_ACTIVE',
          'A recording session is already active'
        ),
      };
    }

    // Optimistic transition so this webview reflects `armed` immediately; the
    // `arm_state_changed` event the command emits keeps the other webview in
    // sync (and is a no-op here once we are already armed).
    this.transition({ kind: 'armed', agent: null });
    try {
      const invokeApi = await this.getInvokeApi();
      await invokeApi.invoke<void>(RECORDING_ARM, { entrySurface });
    } catch (raw) {
      this.transition({ kind: 'idle' });
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }
    return { ok: true };
  }

  async disarm(): Promise<ArmResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    // Block only while a session is in flight. We still invoke the command from
    // `idle` because a failed record attempt can leave the toolbar window shown
    // with the engine idle; the backend restores the entry surface only when its
    // armed latch is still set, so an idle disarm after a completed session
    // leaves the windows untouched.
    if (
      this.status.kind === 'recording' ||
      this.status.kind === 'starting' ||
      this.status.kind === 'stopping'
    ) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'ALREADY_ACTIVE',
          'A recording session is already active'
        ),
      };
    }

    const wasArmed = this.status.kind === 'armed';
    const armedAgent = this.status.kind === 'armed' ? this.status.agent : null;
    if (wasArmed) this.transition({ kind: 'idle' });
    try {
      const invokeApi = await this.getInvokeApi();
      await invokeApi.invoke<void>(RECORDING_DISARM);
    } catch (raw) {
      if (wasArmed) this.transition({ kind: 'armed', agent: armedAgent });
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }
    return { ok: true };
  }

  async start(
    name: string,
    trayLabels: RecordingTrayLabels,
    options?: RecordingStartOptions
  ): Promise<StartResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    if (this.status.kind !== 'idle' && this.status.kind !== 'armed') {
      return {
        ok: false,
        error: new RpaRecorderError(
          'ALREADY_ACTIVE',
          'A recording session is already active'
        ),
      };
    }

    const armedStatusBeforeStart = this.status.kind === 'armed' ? this.status : null;
    const captureNarration = options?.captureNarration ?? false;
    const agent = armedStatusBeforeStart?.agent ?? null;
    this.transition({ kind: 'starting', name, captureNarration, agent });

    let sessionId: SessionId;
    try {
      const invokeApi = await this.getInvokeApi();
      // `entrySurface: undefined` is dropped by invoke's JSON serialization, so
      // toolbar starts (which omit it) keep the surface latched at arm().
      sessionId = await invokeApi.invoke<SessionId>(RECORDING_START, {
        request: { name, description: null, captureNarration },
        trayLabels,
        entrySurface: options?.entrySurface,
      });
    } catch (raw) {
      const error = recordingErrorFromUnknown(raw);
      this.transition(armedStatusBeforeStart ?? { kind: 'idle' });
      return { ok: false, error };
    }

    // The Started event may have raced ahead of the command response.
    // Reading via getStatus() defeats TS narrowing locked in by the
    // earlier idle-guard.
    const postInvokeStatus = this.getStatus();
    if (
      postInvokeStatus.kind === 'recording' &&
      postInvokeStatus.sessionId === sessionId
    ) {
      return { ok: true, sessionId };
    }

    try {
      await new Promise<void>((resolve, reject) => {
        this.pendingStart = { sessionId, resolve, reject };
      });
    } catch (raw) {
      const error =
        raw instanceof RpaRecorderError ? raw : recordingErrorFromUnknown(raw);
      return { ok: false, error };
    }
    return { ok: true, sessionId };
  }

  async restart(
    name: string,
    trayLabels: RecordingTrayLabels,
    options?: RecordingStartOptions
  ): Promise<StartResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    if (this.status.kind !== 'recording') {
      return {
        ok: false,
        error: new RpaRecorderError('NOT_ACTIVE', 'No recording session is active'),
      };
    }

    // Stay in `recording` (old session) until the new Started arrives, so the
    // timer keeps ticking rather than blanking through a `starting` flash.
    // Seed the new name now so the Started handler (which sees `recording`, not
    // `starting`) carries it onto the fresh session — even if Started races
    // ahead of the invoke response.
    const captureNarration = options?.captureNarration ?? false;
    this.startedSessionSeed = {
      name,
      captureNarration,
      agent: this.status.agent,
    };
    let sessionId: SessionId;
    try {
      const invokeApi = await this.getInvokeApi();
      sessionId = await invokeApi.invoke<SessionId>(RECORDING_RESTART, {
        request: {
          name,
          description: null,
          captureNarration,
        },
        trayLabels,
      });
    } catch (raw) {
      // Restart failed. If the engine rejected before aborting (not recording),
      // the current take is untouched; once the old take is aborted, the engine
      // emits SessionFailed and the bridge moves to `failed` via the event
      // listener (no seed is consumed, so drop it here).
      this.startedSessionSeed = null;
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }

    // The new session's Started event may have raced ahead of the response.
    const postInvokeStatus = this.getStatus();
    if (
      postInvokeStatus.kind === 'recording' &&
      postInvokeStatus.sessionId === sessionId
    ) {
      return { ok: true, sessionId };
    }

    try {
      await new Promise<void>((resolve, reject) => {
        this.pendingStart = { sessionId, resolve, reject };
      });
    } catch (raw) {
      const error =
        raw instanceof RpaRecorderError ? raw : recordingErrorFromUnknown(raw);
      return { ok: false, error };
    }
    return { ok: true, sessionId };
  }

  async stop(): Promise<StopResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    if (this.status.kind !== 'recording') {
      return {
        ok: false,
        error: new RpaRecorderError('NOT_ACTIVE', 'No recording session is active'),
      };
    }

    const activeSessionId = this.status.sessionId;
    this.transition({
      kind: 'stopping',
      sessionId: activeSessionId,
      name: this.status.name,
      startedAt: this.status.startedAt,
      captureNarration: this.status.captureNarration,
      narrationMicLive: this.status.narrationMicLive,
      agent: this.status.agent,
    });

    let summary: SessionSummary;
    try {
      const invokeApi = await this.getInvokeApi();
      summary = await invokeApi.invoke<SessionSummary>(RECORDING_STOP);
    } catch (raw) {
      this.scheduleStopRecovery(activeSessionId);
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }

    const postInvokeStatus = this.getStatus();
    if (postInvokeStatus.kind === 'idle') {
      return { ok: true, summary };
    }

    try {
      await new Promise<void>((resolve, reject) => {
        this.pendingStop = { sessionId: activeSessionId, resolve, reject };
        this.scheduleStopEventFallback(activeSessionId);
      });
    } catch (raw) {
      const error =
        raw instanceof RpaRecorderError ? raw : recordingErrorFromUnknown(raw);
      return { ok: false, error };
    }
    return { ok: true, summary };
  }

  async cancelActiveRecording(): Promise<ArmResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    if (this.status.kind !== 'recording') {
      return {
        ok: false,
        error: new RpaRecorderError('NOT_ACTIVE', 'No recording session is active'),
      };
    }

    try {
      const invokeApi = await this.getInvokeApi();
      await invokeApi.invoke<void>(RECORDING_CANCEL);
      this.transition({ kind: 'armed', agent: this.status.agent });
      return { ok: true };
    } catch (raw) {
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }
  }

  async listRecordings(): Promise<readonly Workflow[]> {
    if (!this.deps.isTauri()) return [];
    const invokeApi = await this.getInvokeApi();
    try {
      const records = await invokeApi.invoke<RecordingRecord[]>(RECORDING_LIST);
      return records.map(workflowFromRecord);
    } catch (raw) {
      throw recordingErrorFromUnknown(raw);
    }
  }

  async discardRecording(sessionId: SessionId): Promise<DiscardResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    try {
      const invokeApi = await this.getInvokeApi();
      await invokeApi.invoke<void>(RECORDING_DISCARD, { sessionId });
      return { ok: true };
    } catch (raw) {
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }
  }

  async discardRecordingWhenReady(sessionId: SessionId): Promise<DiscardResult> {
    const immediate = await this.discardRecording(sessionId);
    if (immediate.ok || immediate.error.code !== 'SESSION_IN_FLIGHT') {
      return immediate;
    }

    return await new Promise<DiscardResult>((resolve) => {
      let settled = false;
      const finish = (result: DiscardResult) => {
        if (settled) return;
        settled = true;
        unsubscribe();
        clearTimeout(timeoutId);
        resolve(result);
      };

      const unsubscribe = this.subscribe(() => {
        const status = this.getStatus();
        if (this.isSessionBusyForDiscard(status, sessionId)) return;
        void this.discardRecording(sessionId).then(finish);
      });

      const timeoutId = setTimeout(() => {
        finish({
          ok: false,
          error: new RpaRecorderError(
            'STOP_TIMED_OUT',
            'Timed out waiting to discard the recording session'
          ),
        });
      }, STOP_RECOVERY_TIMEOUT_MS);
    });
  }

  async submitRecordingName(
    sessionId: SessionId,
    name: string,
    description?: string | null
  ): Promise<SubmitRecordingNameResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }

    const validated = validateRecordingName(name);
    if (!validated.ok) {
      return {
        ok: false,
        error: new RpaRecorderError(validated.code, engineErrorMessage(validated.code)),
      };
    }

    try {
      const invokeApi = await this.getInvokeApi();
      await invokeApi.invoke<void>(RECORDING_NAME_SUBMIT, {
        request: {
          sessionId,
          name: validated.name,
          description: normalizeRecordingDescription(description),
        },
      });
      return { ok: true };
    } catch (raw) {
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }
  }

  private isSessionBusyForDiscard(
    status: RpaRecorderStatus,
    sessionId: SessionId
  ): boolean {
    switch (status.kind) {
      case 'starting':
        return true;
      case 'recording':
      case 'stopping':
        return status.sessionId === sessionId;
      case 'idle':
      case 'armed':
      case 'failed':
        return false;
    }
  }

  async recoverFailedSessionAndStart(
    name: string,
    trayLabels: RecordingTrayLabels,
    options?: RecordingStartOptions
  ): Promise<StartResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }

    // The engine omits the failed session id from start-error payloads, so
    // we fetch authoritative engine status here rather than relying on
    // `this.status` (which may already be `idle` after the error).
    let engineStatus: EngineSessionStatus;
    try {
      const invokeApi = await this.getInvokeApi();
      engineStatus = await invokeApi.invoke<EngineSessionStatus>(RECORDING_STATUS);
    } catch (raw) {
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }

    if (engineStatus.type !== 'failed') {
      return {
        ok: false,
        error: new RpaRecorderError(
          'FAILED_SESSION_PENDING_DISCARD',
          'No failed recording session is pending discard'
        ),
      };
    }

    const discard = await this.discardRecording(engineStatus.sessionId);
    if (!discard.ok) return { ok: false, error: discard.error };

    // Bridge may still carry a `failed` status from the prior session-state
    // event; reset it so `start()` can pass its idle guard.
    if (this.status.kind === 'failed') {
      this.transition({ kind: 'idle' });
    }
    return this.start(name, trayLabels, options);
  }

  async setToolbarMode(mode: RecordingToolbarMode): Promise<void> {
    if (!this.deps.isTauri()) return;
    try {
      const invokeApi = await this.getInvokeApi();
      await invokeApi.invoke<void>(RECORDING_TOOLBAR_SET_MODE, { mode });
    } catch (raw) {
      // Panel mode is cosmetic best-effort; never let it surface to the confirm flow.
      logger.warn('[rpa-recorder] setToolbarMode failed', {
        code: recordingErrorFromUnknown(raw).code,
      });
    }
  }

  async subscribeToolbarHover(
    listener: (hovering: boolean) => void
  ): Promise<Unsubscribe> {
    if (!this.deps.isTauri()) return () => {};
    try {
      const eventApi = await this.getEventApi();
      return await eventApi.listen<ToolbarHoverChangedPayload>(
        TOOLBAR_HOVER_CHANGED_EVENT,
        (event) => {
          listener(event.payload.hovering);
        }
      );
    } catch (raw) {
      // Hover is a cosmetic enhancement; if the dynamic event-API import or
      // `listen()` rejects, degrade to a no-op unsubscribe rather than rejecting
      // — callers (e.g. useRecorderToolbarHover) fire-and-forget this promise.
      logger.warn('[rpa-recorder] subscribeToolbarHover failed', {
        code: recordingErrorFromUnknown(raw).code,
      });
      return () => {};
    }
  }

  async subscribeNativeStopRequested(listener: () => void): Promise<Unsubscribe> {
    if (!this.deps.isTauri()) return () => {};
    try {
      const eventApi = await this.getEventApi();
      return await eventApi.listen<void>(NATIVE_STOP_REQUESTED_EVENT, () => {
        listener();
      });
    } catch (raw) {
      // Best effort only; a missed native stop request just means the tray
      // item / shortcut silently no-ops instead of stopping the recording.
      logger.warn('[rpa-recorder] subscribeNativeStopRequested failed', {
        code: recordingErrorFromUnknown(raw).code,
      });
      return () => {};
    }
  }

  async subscribeNarrationLevels(
    listener: (levels: NarrationLevelBands) => void
  ): Promise<Unsubscribe> {
    if (!this.deps.isTauri()) return () => {};
    try {
      const eventApi = await this.getEventApi();
      return await eventApi.listen<NarrationAudioLevelsPayload>(
        NARRATION_AUDIO_LEVELS_EVENT,
        (event) => {
          listener(event.payload.levels);
        }
      );
    } catch (raw) {
      // The meter is cosmetic; degrade to a resting indicator rather than
      // rejecting a promise that callers fire and forget.
      logger.warn('[rpa-recorder] subscribeNarrationLevels failed', {
        code: recordingErrorFromUnknown(raw).code,
      });
      return () => {};
    }
  }

  async subscribeAgentPermissionRequested(
    listener: (request: AgentPermissionRequestedPayload) => void
  ): Promise<Unsubscribe> {
    if (!this.deps.isTauri()) return () => {};
    const eventApi = await this.getEventApi();
    return eventApi.listen<AgentPermissionRequestedPayload>(
      AGENT_PERMISSION_REQUESTED_EVENT,
      (event) => {
        listener(event.payload);
      }
    );
  }

  async acknowledgeAgentPermission(
    ack: AgentPermissionAckRequest
  ): Promise<AgentPermissionAckResult> {
    if (!this.deps.isTauri()) {
      return {
        ok: false,
        error: new RpaRecorderError(
          'BRIDGE_UNAVAILABLE',
          'RpaRecorder is only available inside the Tauri runtime'
        ),
      };
    }
    try {
      const invokeApi = await this.getInvokeApi();
      await invokeApi.invoke<void>(RECORDING_AGENT_PERMISSION_ACK, {
        request: {
          sessionId: ack.sessionId,
          toolCallId: ack.toolCallId,
          requestId: ack.requestId,
          granted: ack.granted,
        },
      });
      return { ok: true };
    } catch (raw) {
      return { ok: false, error: recordingErrorFromUnknown(raw) };
    }
  }

  private applyEngineStatusSeed(engineStatus: EngineSessionStatus): void {
    switch (engineStatus.type) {
      case 'idle':
        // An event may have raced in between listener-attach and seed-fetch
        // — don't overwrite a more advanced state.
        return;
      case 'recording':
        if (
          this.status.kind === 'recording' &&
          this.status.sessionId === engineStatus.sessionId
        ) {
          return;
        }
        this.transition({
          kind: 'recording',
          sessionId: engineStatus.sessionId,
          name: '',
          startedAt: this.deps.now(),
          captureNarration: engineStatus.captureNarration,
          narrationMicLive: engineStatus.narrationMicLive,
          agent: null,
        });
        return;
      case 'failed':
        this.transition({
          kind: 'failed',
          sessionId: engineStatus.sessionId,
          error: new RpaRecorderError(
            ENGINE_ERROR_CODE_MAP[engineStatus.errorCode] ?? 'UNKNOWN',
            engineStatus.errorCode
          ),
        });
        return;
    }
  }

  private applyArmStateSeed(armState: ArmStatePayload): void {
    if (
      armState.agent &&
      (this.status.kind === 'recording' || this.status.kind === 'stopping')
    ) {
      this.transition({ ...this.status, agent: armState.agent });
      return;
    }
    // Only seed from idle: the engine-status seed (or a session-state event
    // racing in during init) reports recording/failed, which must win over the
    // arm latch. Mirrors the idle guard in `applyEngineStatusSeed`.
    if (armState.armed && this.status.kind === 'idle') {
      this.transition(armedStatus(armState.agent));
    }
  }

  private handleArmStateEvent(payload: ArmStateChangedPayload): void {
    if (payload.armed) {
      // Only transition from `idle`; never clobber an in-flight session.
      if (this.status.kind === 'idle') this.transition(armedStatus(payload.agent));
      return;
    }
    if (this.status.kind === 'armed') this.transition({ kind: 'idle' });
  }

  private handlePlatformHealthEvent(payload: PlatformHealthChangedPayload): void {
    // Rust owns the Datadog row for this transition (`[rpa] recording_health`);
    // this is the local breadcrumb only.
    logger.debug('[rpa-recorder] platform health changed', payload);
  }

  private handleFrameCaptureMissedEvent(payload: FrameCaptureMissedPayload): void {
    logger.debug('[rpa-recorder] frame capture missed', payload);
  }

  private handleNarrationMicStateEvent(payload: NarrationMicStateChangedPayload): void {
    if (
      this.status.kind === 'recording' &&
      this.status.sessionId === payload.sessionId &&
      this.status.captureNarration
    ) {
      this.transition({
        ...this.status,
        narrationMicLive: payload.micLive,
      });
      return;
    }
    const mayStillStart =
      this.status.kind === 'starting' ||
      this.startedSessionSeed !== null ||
      this.pendingStart?.sessionId === payload.sessionId;
    if (mayStillStart) {
      this.pendingNarrationMicLive.set(payload.sessionId, payload.micLive);
    } else {
      // Ignore trailing mic-off events after stop/failure and events for an
      // unrelated session; neither can seed a future Started transition.
      this.pendingNarrationMicLive.delete(payload.sessionId);
    }
  }

  private handleSessionStateEvent(payload: SessionStateChangedPayload): void {
    switch (payload.state) {
      case 'started': {
        const seed = this.startedSessionSeed;
        const name =
          seed?.name ?? (this.status.kind === 'starting' ? this.status.name : '');
        const captureNarration =
          seed?.captureNarration ??
          (this.status.kind === 'starting' ? this.status.captureNarration : false);
        const agent =
          seed?.agent ?? (this.status.kind === 'starting' ? this.status.agent : null);
        this.startedSessionSeed = null;
        const pendingMic = this.pendingNarrationMicLive.get(payload.sessionId);
        if (pendingMic !== undefined) {
          this.pendingNarrationMicLive.delete(payload.sessionId);
        }
        this.transition({
          kind: 'recording',
          sessionId: payload.sessionId,
          name,
          startedAt: this.deps.now(),
          captureNarration,
          narrationMicLive: pendingMic ?? false,
          agent,
        });
        if (this.pendingStart && this.pendingStart.sessionId === payload.sessionId) {
          this.pendingStart.resolve();
          this.pendingStart = null;
        }
        return;
      }
      case 'stopping': {
        if (
          this.status.kind === 'recording' &&
          this.status.sessionId === payload.sessionId
        ) {
          this.transition({
            kind: 'stopping',
            sessionId: this.status.sessionId,
            name: this.status.name,
            startedAt: this.status.startedAt,
            captureNarration: this.status.captureNarration,
            narrationMicLive: this.status.narrationMicLive,
            agent: this.status.agent,
          });
        }
        return;
      }
      case 'stopped': {
        this.pendingNarrationMicLive.delete(payload.sessionId);
        this.clearStopRecoveryTimeout();
        this.transition({ kind: 'idle' });
        if (this.pendingStop && this.pendingStop.sessionId === payload.sessionId) {
          this.pendingStop.resolve();
          this.pendingStop = null;
        }
        if (this.pendingStart && this.pendingStart.sessionId === payload.sessionId) {
          this.pendingStart.reject(
            new RpaRecorderError(
              'UNKNOWN',
              'Session ended before reaching the Started state'
            )
          );
          this.pendingStart = null;
        }
        return;
      }
      case 'failed': {
        this.pendingNarrationMicLive.delete(payload.sessionId);
        const error = new RpaRecorderError(
          ENGINE_ERROR_CODE_MAP[payload.errorCode ?? ''] ?? 'UNKNOWN',
          payload.errorCode ?? 'Recording failed'
        );
        this.clearStopRecoveryTimeout();
        this.transition({
          kind: 'failed',
          sessionId: payload.sessionId,
          error,
        });
        if (this.pendingStart && this.pendingStart.sessionId === payload.sessionId) {
          this.pendingStart.reject(error);
          this.pendingStart = null;
        }
        if (this.pendingStop && this.pendingStop.sessionId === payload.sessionId) {
          this.pendingStop.reject(error);
          this.pendingStop = null;
        }
        return;
      }
    }
  }

  private transition(next: RpaRecorderStatus): void {
    if (next.kind !== 'stopping') {
      this.clearStopRecoveryTimeout();
    }
    if (statusEquals(this.status, next)) return;
    this.status = next;
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (err) {
        logger.warn('[rpa-recorder] listener threw', { errorKind: bridgeErrorKind(err) });
      }
    }
  }
}

function statusEquals(a: RpaRecorderStatus, b: RpaRecorderStatus): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'idle':
      return true;
    case 'armed':
      return b.kind === 'armed' && agentModeEquals(a.agent, b.agent);
    case 'starting':
      return (
        b.kind === 'starting' &&
        a.name === b.name &&
        a.captureNarration === b.captureNarration &&
        agentModeEquals(a.agent, b.agent)
      );
    case 'recording':
      return (
        b.kind === 'recording' &&
        a.sessionId === b.sessionId &&
        a.startedAt === b.startedAt &&
        a.name === b.name &&
        a.captureNarration === b.captureNarration &&
        a.narrationMicLive === b.narrationMicLive &&
        agentModeEquals(a.agent, b.agent)
      );
    case 'stopping':
      return (
        b.kind === 'stopping' &&
        a.sessionId === b.sessionId &&
        a.startedAt === b.startedAt &&
        a.name === b.name &&
        a.captureNarration === b.captureNarration &&
        a.narrationMicLive === b.narrationMicLive &&
        agentModeEquals(a.agent, b.agent)
      );
    case 'failed':
      return (
        b.kind === 'failed' &&
        a.sessionId === b.sessionId &&
        a.error.code === b.error.code
      );
  }
}

function agentModeEquals(
  a: AgentModeMetadata | null | undefined,
  b: AgentModeMetadata | null | undefined
): boolean {
  return (
    a?.sessionId === b?.sessionId &&
    a?.toolCallId === b?.toolCallId &&
    a?.requestId === b?.requestId &&
    a?.phase === b?.phase
  );
}

export const rpaRecorderBridge: RpaRecorderBridge = new RpaRecorderBridgeImpl();

export function createRpaRecorderBridge(deps?: Partial<BridgeDeps>): RpaRecorderBridge {
  return new RpaRecorderBridgeImpl({ ...defaultDeps, ...deps });
}
